import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { todayStr } from "../lib/format.js";

const SELECT =
  "id,equipment_id,date,type,hours,parts,labor,fuel,other,cost,performed_by,notes,next_due,equipment(name)";

export function rowToForm(row = {}) {
  return {
    equipmentId: row.equipment_id ?? "",
    date: row.date ?? todayStr(),
    type: row.type ?? "service",
    hours: row.hours ?? "",
    performedBy: row.performed_by ?? "",
    notes: row.notes ?? "",
    nextDue: row.next_due ?? "",
    parts: row.parts ?? "",
    labor: row.labor ?? "",
    fuel: row.fuel ?? "",
    other: row.other ?? "",
  };
}

const blankToNull = (v) => (v === "" || v === undefined ? null : v);
const money = (v) => (v === "" || v === undefined || v === null ? 0 : Number(v) || 0);

export function formToRow(f) {
  return {
    equipment_id: f.equipmentId,
    date: f.date,
    type: f.type,
    hours: blankToNull(f.hours),
    parts: money(f.parts),
    labor: money(f.labor),
    fuel: money(f.fuel),
    other: money(f.other),
    // `cost` is `generated always as (parts+labor+fuel+other) stored`. Sending
    // it would be rejected by Postgres — it is computed, never supplied.
    performed_by: blankToNull(f.performedBy),
    notes: blankToNull(f.notes),
    next_due: blankToNull(f.nextDue),
  };
}

export function useMaintenance() {
  return useQuery({
    queryKey: ["maintenance"],
    queryFn: async () => {
      const { data, error } = await supabase.from("maintenance").select(SELECT).order("date", { ascending: false });
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useEquipmentOptions() {
  return useQuery({
    queryKey: ["equipment", "names"],
    queryFn: async () => {
      const { data, error } = await supabase.from("equipment").select("id,name").order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, form }) => {
      const row = formToRow(form);
      const q = id
        ? supabase.from("maintenance").update(row).eq("id", id).select("id")
        : supabase.from("maintenance").insert([row]).select("id");
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      if (!data?.length) {
        throw new Error(
          "The database accepted the request but changed nothing — usually a row level security policy."
        );
      }

      // v10 also pushed the hour reading and next-service date back onto the
      // machine, so the fleet list stays current. Kept, but only for values
      // actually entered — a blank field must not wipe what is already there.
      const patch = {};
      if (form.hours) patch.hour_meter = form.hours;
      if (form.nextDue) patch.next_service_date = form.nextDue;
      if (Object.keys(patch).length) {
        const { error: eqErr } = await supabase.from("equipment").update(patch).eq("id", form.equipmentId);
        // The service record is saved either way; report the partial result
        // rather than implying the whole save failed.
        if (eqErr) throw new Error(`Service record saved, but the machine's details could not be updated: ${eqErr.message}`);
      }

      return data[0];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      qc.invalidateQueries({ queryKey: ["equipment"] });
    },
  });
}

export function useDeleteMaintenance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data, error } = await supabase.from("maintenance").delete().eq("id", id).select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maintenance"] }),
  });
}
